'use client';

import { Button, Divider } from '@heroui/react';
import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { authClient } from '@/lib/auth-client';
import {
  authMagicLinkSendFailed,
  authMagicLinkSent,
  authSocialSignInStarted,
} from '@/util/functions/observability/frontendLogger';
import Image from 'next/image';
import posthog from 'posthog-js';
import { EVENTS } from '@attik/posthog';

type formBody = {
  email: string;
};

/** Same rules as verify-magic-link route: relative path only, no protocol-relative URLs. */
function safeCallbackPath(raw: string | null): string {
  const s = (raw ?? '').trim();
  if (!s || !s.startsWith('/') || s.includes('//')) {
    return '/';
  }
  return s;
}

/** Coarse, non-PII hint for pre-session auth logs (no full email). */
function emailDomainFromAddress(email: string): string | undefined {
  const e = email.trim().toLowerCase();
  const at = e.lastIndexOf('@');
  if (at < 1 || at === e.length - 1) return undefined;
  const d = e.slice(at + 1);
  return d || undefined;
}

export default function LoginForm() {
  const query = useSearchParams();
  const callbackPath = safeCallbackPath(query.get('callbackUrl'));
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<formBody>();

  const sendMagicLink = async (formData: formBody) => {
    setSending(true);
    setError(null);
    const emailDomain = emailDomainFromAddress(formData.email);
    const magicLinkLogBase = {
      callbackPath,
      ...(emailDomain ? { emailDomain } : {}),
    };
    try {
      const { error: authError } = await authClient.signIn.magicLink({
        email: formData.email,
        callbackURL: callbackPath,
      });

      if (authError) {
        authMagicLinkSendFailed(authError.message || 'Failed to send magic link', {
          ...magicLinkLogBase,
        });
        setError(authError.message || 'Failed to send magic link');
        return;
      }

      authMagicLinkSent(magicLinkLogBase);
      posthog.identify(formData.email, { email: formData.email });
      posthog.capture(EVENTS.loginMagicLinkSent, { email_domain: emailDomain });
      setSentTo(formData.email);
    } catch (err) {
      authMagicLinkSendFailed(err instanceof Error ? err : String(err), {
        ...magicLinkLogBase,
      });
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
    } finally {
      setSending(false);
    }
  };

  const googleSignIn = () => {
    authSocialSignInStarted('google', { callbackPath });
    posthog.capture(EVENTS.loginGoogleClicked);
    authClient.signIn.social({
      provider: 'google',
      callbackURL: new URL(callbackPath, window.location.origin).href,
      errorCallbackURL: `${window.location.origin}/login`,
    });
  };

  if (sentTo) {
    return (
      <div className='flex flex-col gap-3 lg:gap-4'>
        <h1 className='text-[28px] font-bold leading-[1.15] text-white lg:text-[40px]'>
          Check your email
        </h1>
        <p className='text-[15px] leading-normal text-gray-400 lg:text-base'>
          We sent a login link to{' '}
          <strong className='text-white'>{sentTo}</strong>.
        </p>
        <button
          type='button'
          onClick={() => setSentTo(null)}
          className='self-start text-[15px] font-medium text-primary hover:text-white'
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-[14px] lg:gap-8'>
      <h1 className='mb-2 text-pretty text-[28px] font-bold leading-[1.15] text-white lg:mb-0 lg:text-[40px]'>
        Welcome back.
      </h1>
      <form
        onSubmit={handleSubmit(sendMagicLink)}
        className='flex flex-col gap-[14px] lg:gap-3'
      >
        <label className='flex flex-col gap-2'>
          <span className='sr-only text-sm font-medium text-gray-400 lg:not-sr-only'>
            Work email
          </span>
          <input
            {...register('email', { required: true, pattern: /^\S+@\S+$/i })}
            id='email-postmark'
            type='email'
            placeholder='john@example.com'
            aria-invalid={!!errors.email}
            className='h-[50px] rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] px-4 text-base text-white outline-none placeholder:text-gray-500 focus:border-primary focus:ring-[3px] focus:ring-primary/30 aria-[invalid=true]:border-red-500'
          />
        </label>
        <Button
          type='submit'
          isLoading={sending}
          className='h-[50px] rounded-lg bg-primary text-base font-bold uppercase text-white data-[hover=true]:bg-[#b94800] data-[hover=true]:opacity-100'
        >
          Send login link
        </Button>
        <Divider className='my-1 bg-[#2e2e2e]' />
        <Button
          type='button'
          onPress={googleSignIn}
          className='h-[50px] gap-2.5 rounded-lg bg-white text-base font-bold uppercase text-[#0c0c0c] data-[hover=true]:bg-[#edf2f4] data-[hover=true]:opacity-100'
        >
          <Image src='/Google_Logo.svg' alt='' width={18} height={18} />
          Continue with Google
        </Button>
      </form>
      {error && (
        <div className='rounded-lg bg-red-50 p-3 text-sm text-red-700'>
          {error}
        </div>
      )}
    </div>
  );
}
